import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAbpPermissions } from '../contexts/AbpPermissionsContext';

interface AppLoadingScreenProps {
  children: ReactNode;
}

/**
 * AppLoadingScreen - Shows loading screen while tenant settings are being fetched
 *
 * Prevents FOUC (Flash of Unstyled Content) by waiting for ABP configuration
 * to load before rendering the app. This ensures tenant branding (name, logo,
 * theme CSS) is available from the very first render.
 */
export function AppLoadingScreen({ children }: AppLoadingScreenProps) {
  const { t } = useTranslation();
  const { isLoading } = useAbpPermissions();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="text-center space-y-6">
          {/* Animated loader */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 opacity-20 animate-pulse"></div>
            </div>
            <div className="relative flex items-center justify-center">
              <Loader2 className="w-16 h-16 text-primary-600 animate-spin" />
            </div>
          </div>

          {/* Loading text */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {t('App.Loading.Title')}
            </h2>
            <p className="text-neutral-600">
              {t('App.Loading.Subtitle')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
